import { useMemo, useState } from "react";
import { gql, useQuery } from "@apollo/client";
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css";
import Header from "../../../../../components/common/header";
import LoadingSpinnerFull from "../../../../../components/common/loading-spinner/loading-spinner-full";
import { isApp } from "../../../../../utils/user-agent";
import type { Sale } from "../../../../../__generated__/graphql";
import {
  formatDateToYearMonth,
  formatDateToYearMonthDay,
} from "../../../../../utils/date/format-date";
import MonthNavigator, {
  isSettlementMonthOpen,
} from "../components/month-navigator";
import SettlementOrderTable, {
  type SettlementOrder,
} from "../components/settlement-order-table";

// ─── Queries ────────────────────────────────────────────────────────────────

const GET_STORE_ORDERS = gql`
  query GetSettlementDetailOrders(
    $type: String!
    $status: String!
    $startDate: String!
    $endDate: String!
    $settled: Boolean
  ) {
    getStoreOrders(
      type: $type
      status: $status
      startDate: $startDate
      endDate: $endDate
      settled: $settled
    ) {
      id
      sellerAmount
      sellerAmountWithFee
      createdAt
      packId
      totalQuantity
      pack {
        id
      }
      products {
        id
      }
    }
  }
`;

const GET_APPROVED_ORDERS = gql`
  query OrdersApprovedByMonthCalendar($yearMonth: String!) {
    ordersApprovedByMonth(yearMonth: $yearMonth) {
      date
      sales {
        id
        name
        price
        sellerAmount
        sellerAmountWithFee
        fee
        soldAt
        isSettled
      }
      totalSellerAmount
    }
  }
`;

// ─── Types ───────────────────────────────────────────────────────────────────

type ViewMode = "calendar" | "list";

/** 리스트뷰: 지급 완료 vs 정산 예정(미지급) */
type ListSettlementScope = "completed" | "pending";

// ─── Helpers ─────────────────────────────────────────────────────────────────

function getMonthRange(date: Date) {
  const start = new Date(date.getFullYear(), date.getMonth(), 1);
  const end = new Date(date.getFullYear(), date.getMonth() + 1, 0);
  return {
    startDate: toLocalDateStr(start),
    endDate: toLocalDateStr(end),
  };
}

function toLocalDateStr(d: Date) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

const LEGACY_CUTOFF_NOTICE = "2025/09/07 이전의 내역은 고객센터 문의바랍니다";
const API_URL = process.env.REACT_APP_API_URL;

// ─── Calendar styles ──────────────────────────────────────────────────────────

const calendarStyles = `
  .react-calendar {
    width: 100% !important;
    max-width: none !important;
    border: none !important;
  }
  .react-calendar__tile {
    text-align: left !important;
    padding: 1em 0.5em !important;
  }
  .react-calendar__month-view__days__day {
    display: flex !important;
    flex-direction: column !important;
    align-items: center !important;
    text-align: center !important;
  }
  .react-calendar__tile--active {
    border: 2px solid #2563eb !important;
    background: none !important;
    color: inherit !important;
  }
  .react-calendar__tile--now {
    background: #2563eb !important;
    color: white !important;
  }
  .react-calendar__month-view__days__day--weekend {
    color: inherit !important;
  }
`;

// ─── Component ───────────────────────────────────────────────────────────────

export default function SettlementDetail() {
  const [viewMode, setViewMode] = useState<ViewMode>("list");
  const [currentMonth, setCurrentMonth] = useState(() => new Date());

  const currentYearMonth = formatDateToYearMonth(currentMonth, "-");
  const { startDate, endDate } = getMonthRange(currentMonth);

  const [selectedDate, setSelectedDate] = useState(new Date());
  const [showDayModal, setShowDayModal] = useState(false);
  const [listSettlementScope, setListSettlementScope] =
    useState<ListSettlementScope>("completed");

  const listSettled = listSettlementScope === "completed";

  // ── 리스트뷰 쿼리 (지급 완료 또는 정산 예정)
  const { data: listData, loading: listLoading } = useQuery(GET_STORE_ORDERS, {
    variables: {
      type: "ALL",
      status: "CONFIRMED",
      startDate: new Date(startDate + "T00:00:00").toISOString(),
      endDate: new Date(endDate + "T23:59:59").toISOString(),
      settled: listSettled,
    },
    fetchPolicy: "cache-first",
    skip: viewMode !== "list",
  });

  // ── 캘린더뷰 쿼리
  const {
    data: calData,
    loading: calLoading,
    refetch: calRefetch,
  } = useQuery(GET_APPROVED_ORDERS, {
    variables: { yearMonth: currentYearMonth },
    fetchPolicy: "cache-first",
    skip: viewMode !== "calendar",
  });

  // ── 리스트뷰 데이터 가공
  const orders: SettlementOrder[] = useMemo(() => {
    if (!listData?.getStoreOrders) return [];
    return [...(listData.getStoreOrders as SettlementOrder[])]
      .filter((o): o is SettlementOrder => o != null)
      .sort(
        (a, b) =>
          new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
      );
  }, [listData]);

  const totalSellerAmount = useMemo(
    () => orders.reduce((sum, o) => sum + (o.sellerAmount || 0), 0),
    [orders],
  );
  const totalSettlement = useMemo(
    () => orders.reduce((sum, o) => sum + (o.sellerAmountWithFee || 0), 0),
    [orders],
  );

  // ── 캘린더뷰 헬퍼
  const getDailySalesData = (date: Date) => {
    if (!calData?.ordersApprovedByMonth) return null;
    const dateStr = formatDateToYearMonthDay(date, "-");
    return calData.ordersApprovedByMonth.find(
      (entry: { date: string }) => entry.date === dateStr,
    );
  };

  // ── 월 네비게이션
  const goToPrevMonth = () => {
    const prev = new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1);
    setCurrentMonth(prev);
    if (viewMode === "calendar") calRefetch({ yearMonth: formatDateToYearMonth(prev, "-") });
  };

  const goToNextMonth = () => {
    const next = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1);
    if (!isSettlementMonthOpen(next)) return;
    setCurrentMonth(next);
    if (viewMode === "calendar") calRefetch({ yearMonth: formatDateToYearMonth(next, "-") });
  };

  // ── 다운로드 (리스트뷰)
  const buildExportUrl = (format: "csv" | "xlsx") => {
    const sellerToken = localStorage.getItem("sellerToken");
    const params = new URLSearchParams({
      type: "ALL",
      status: "CONFIRMED",
      startDate: new Date(startDate + "T00:00:00").toISOString(),
      endDate: new Date(endDate + "T23:59:59").toISOString(),
      settled: String(listSettled),
      format,
    });
    return {
      url: `${API_URL}/seller/settlement-orders/export?${params.toString()}`,
      token: sellerToken,
    };
  };

  const handleDownload = (format: "csv" | "xlsx") => {
    if (orders.length === 0) return;
    const { url, token } = buildExportUrl(format);
    const fullUrl = `${url}&_token=${encodeURIComponent(token || "")}`;
    if (isApp()) {
      window.ReactNativeWebView?.postMessage(
        JSON.stringify({ type: "OPEN_EXTERNAL_URL", data: { url: fullUrl } }),
      );
    } else {
      const link = document.createElement("a");
      link.href = fullUrl;
      link.download = `정산내역.${format}`;
      link.click();
    }
  };

  return (
    <>
      <style>{calendarStyles}</style>
      <div className="w-full min-h-screen flex flex-col bg-gray-50">
        <Header title="정산 내역" showBackButton />

        {/* 레거시 안내 배너 */}
        <div className="bg-gray-100 px-4 py-3 flex justify-between items-center border-b border-gray-200">
          <span className="text-sm text-gray-500">{LEGACY_CUTOFF_NOTICE}</span>
          <span className="text-gray-400 text-lg font-light">›</span>
        </div>

        {/* 뷰 토글 */}
        <div className="px-4 pt-4">
          <div className="flex rounded-xl overflow-hidden border border-gray-200 bg-gray-100 p-1 gap-1">
            {(["list", "calendar"] as ViewMode[]).map((mode) => (
              <button
                key={mode}
                type="button"
                onClick={() => setViewMode(mode)}
                className={`flex-1 py-2 text-sm font-semibold rounded-lg transition-colors ${
                  viewMode === mode
                    ? "bg-white text-blue-600 shadow-sm"
                    : "text-gray-500"
                }`}
              >
                {mode === "list" ? "📋 리스트뷰" : "📅 캘린더뷰"}
              </button>
            ))}
          </div>
        </div>

        {/* 월 네비게이션 (리스트뷰일 때 우측에 정산 필터) */}
        <MonthNavigator
          currentMonth={currentMonth}
          onPrev={goToPrevMonth}
          onNext={goToNextMonth}
          endAdornment={
            viewMode === "list" ? (
              <div className="inline-flex shrink-0 rounded-lg border border-gray-200 bg-gray-100 p-0.5 gap-0.5">
                {(
                  [
                    { scope: "completed" as const, label: "지급 완료" },
                    { scope: "pending" as const, label: "정산 예정" },
                  ] as const
                ).map(({ scope, label }) => (
                  <button
                    key={scope}
                    type="button"
                    onClick={() => setListSettlementScope(scope)}
                    className={`whitespace-nowrap px-2 py-1 text-xs font-medium rounded-md transition-colors ${
                      listSettlementScope === scope
                        ? "bg-white text-blue-600 shadow-sm"
                        : "text-gray-500"
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            ) : undefined
          }
        />

        {/* ── 리스트뷰 ──────────────────────────────────────────── */}
        {viewMode === "list" && (
          <>
            {/* 요약 카드 */}
            <div className="mx-4 mt-2 bg-white rounded-xl border border-gray-200 p-5">
              <div className="flex justify-between items-center mb-3">
                <span className="text-gray-600">
                  {listSettled ? "매출액(지급 완료)" : "매출액(정산 예정)"}
                </span>
                <span className="text-xl font-bold text-gray-900">
                  {listLoading ? "..." : `${totalSellerAmount.toLocaleString()}원`}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">
                  {listSettled ? "실 수령액(지급 완료)" : "실 수령액(정산 예정)"}
                </span>
                <span className="text-xl font-bold text-gray-900">
                  {listLoading ? "..." : `${totalSettlement.toLocaleString()}원`}
                </span>
              </div>
            </div>

            {/* 정산 내역 표 */}
            {listLoading ? (
              <LoadingSpinnerFull />
            ) : orders.length > 0 ? (
              <SettlementOrderTable orders={orders} />
            ) : (
              <div className="flex-1 flex items-center justify-center py-16">
                <p className="text-gray-400 text-lg">
                  {listSettled
                    ? "해당 월의 지급 완료된 정산 내역이 없습니다."
                    : "해당 월의 정산 예정인 내역이 없습니다."}
                </p>
              </div>
            )}

            {/* 다운로드 버튼 */}
            <div className="p-4 mt-auto">
              <div className="grid grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => handleDownload("csv")}
                  disabled={orders.length === 0}
                  className="w-full bg-gray-700 disabled:bg-gray-300 text-white py-4 rounded-xl font-semibold text-lg transition-colors"
                >
                  CSV 다운로드
                </button>
                <button
                  type="button"
                  onClick={() => handleDownload("xlsx")}
                  disabled={orders.length === 0}
                  className="w-full bg-blue-400 disabled:bg-gray-300 text-white py-4 rounded-xl font-semibold text-lg transition-colors"
                >
                  XLSX 다운로드
                </button>
              </div>
            </div>
          </>
        )}

        {/* ── 캘린더뷰 ──────────────────────────────────────────── */}
        {viewMode === "calendar" && (
          <div className="mx-4 mt-2 bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div className="relative">
              <Calendar
                onChange={(value) => {
                  setSelectedDate(value as Date);
                  setShowDayModal(true);
                }}
                value={selectedDate}
                className="w-full"
                calendarType="gregory"
                onActiveStartDateChange={(e) => {
                  if (!e.activeStartDate || !isSettlementMonthOpen(e.activeStartDate)) return;
                  const newYM = formatDateToYearMonth(e.activeStartDate, "-");
                  const newMonth = new Date(e.activeStartDate);
                  setCurrentMonth(newMonth);
                  calRefetch({ yearMonth: newYM });
                }}
                activeStartDate={new Date(currentYearMonth + "-01")}
                tileContent={({ date }) => {
                  const dayData = getDailySalesData(date);
                  if (!dayData?.sales?.length) {
                    return <div className="mt-1 py-1">&nbsp;</div>;
                  }
                  const settledAmount = dayData.sales
                    .filter((s: Sale) => s.isSettled)
                    .reduce((sum: number, s: Sale) => sum + (s.sellerAmountWithFee ?? 0), 0);
                  const pendingAmount = dayData.sales
                    .filter((s: Sale) => !s.isSettled)
                    .reduce((sum: number, s: Sale) => sum + (s.sellerAmountWithFee ?? 0), 0);
                  return (
                    <div className="mt-1 border bg-gray-100 py-1 overflow-hidden">
                      {settledAmount > 0 && (
                        <div className="font-bold text-green-500 text-xs">
                          {calLoading ? "-" : settledAmount.toLocaleString()}
                        </div>
                      )}
                      {pendingAmount > 0 && (
                        <div className="font-bold text-rose-500 text-xs">
                          {calLoading ? "-" : pendingAmount.toLocaleString()}
                        </div>
                      )}
                    </div>
                  );
                }}
              />
              {calLoading && (
                <div className="absolute inset-0 bg-white bg-opacity-50 flex items-center justify-center">
                  <div className="flex items-center space-x-2">
                    <div className="w-2 h-2 bg-blue-600 rounded-full animate-bounce [animation-delay:-0.3s]" />
                    <div className="w-2 h-2 bg-blue-600 rounded-full animate-bounce [animation-delay:-0.15s]" />
                    <div className="w-2 h-2 bg-blue-600 rounded-full animate-bounce" />
                  </div>
                </div>
              )}
            </div>

            {/* 범례 */}
            <div className="flex justify-center gap-4 text-sm font-semibold py-3 border-t border-gray-100">
              <div className="flex items-center gap-1">
                <div className="w-2 h-2 rounded-full bg-green-500" />
                <span className="text-green-500">정산 완료</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-2 h-2 rounded-full bg-rose-500" />
                <span className="text-rose-500">정산 예정</span>
              </div>
            </div>
          </div>
        )}

        {/* 일별 상세 모달 (캘린더뷰) */}
        {showDayModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 max-w-xl w-full max-h-[80vh] overflow-y-auto relative mx-4">
              <button
                type="button"
                onClick={() => setShowDayModal(false)}
                className="absolute right-4 top-4 text-gray-500 hover:text-gray-700"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>

              <h3 className="text-lg font-semibold mb-4">
                {formatDateToYearMonthDay(selectedDate)} 판매 내역
              </h3>

              {(() => {
                const dailyData = getDailySalesData(selectedDate);
                if (!dailyData?.sales?.length) {
                  return (
                    <div className="text-center py-8 text-gray-500">
                      해당 날짜의 판매 내역이 없습니다.
                    </div>
                  );
                }
                return (
                  <div className="space-y-4">
                    {dailyData.sales.map((sale: Sale) => (
                      <div key={sale.id} className="border-b py-4">
                        <div className="flex justify-between items-center mb-2">
                          <h3 className="text-base font-semibold">{sale.name}</h3>
                          <span className="text-sm text-gray-500">
                            {new Date(sale.soldAt).getHours().toString().padStart(2, "0")}:
                            {new Date(sale.soldAt).getMinutes().toString().padStart(2, "0")}
                          </span>
                        </div>
                        <div className="space-y-1 text-sm">
                          <div className="flex justify-between">
                            <span className="text-gray-500">정가</span>
                            <span>{sale.price.toLocaleString()}원</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-500">할인가</span>
                            <span>{(sale.sellerAmount ?? 0).toLocaleString()}원</span>
                          </div>
                          <div className="flex justify-between">
                            {sale.fee === 0 ? (
                              <span className="text-green-600 font-semibold">수수료 무료</span>
                            ) : (
                              <>
                                <span className="text-gray-500">수수료(9%)</span>
                                <span>{(sale.fee ?? 0).toLocaleString()}원</span>
                              </>
                            )}
                          </div>
                          <div className="flex justify-between font-bold">
                            <span>정산금액</span>
                            <span>{(sale.sellerAmountWithFee ?? 0).toLocaleString()}원</span>
                          </div>
                        </div>
                      </div>
                    ))}
                    <div className="pt-4 border-t flex justify-between items-center">
                      <span className="text-base font-bold">총 정산금액</span>
                      <span className="text-lg font-bold">
                        {dailyData.totalSellerAmount.toLocaleString()}원
                      </span>
                    </div>
                  </div>
                );
              })()}
            </div>
          </div>
        )}
      </div>
    </>
  );
}
